import { MotivationType } from '../../../../features/market-explorer/types/MotivationType'
import { MotivationAnalysis, MotivationAttributes } from '../types/Motivations'

// Legacy's `MOTIVATION_TYPES_ARRAY` order.
export const motivationTypes: string[] = Object.values(MotivationType).sort()

export const minAttributeValue = 0
export const maxAttributeValue = 5

export type AttributeFormValue = { name: string; value: string }

export type AttributeValueError = 'required' | 'invalid' | 'range'

const sameValue = (a: number | undefined, b: number | undefined) => a === b || (a !== undefined && b !== undefined && Math.abs(a - b) < 1e-9)

const sameAttributes = (a: MotivationAttributes | undefined, b: MotivationAttributes | undefined) => {
  const names = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])
  return [...names].every((name) => sameValue(a?.[name], b?.[name]))
}

// A game without an analysis gets `{}` from the backend.
export const hasMotivationAnalysis = (analysis: MotivationAnalysis | null | undefined): analysis is MotivationAnalysis =>
  !!analysis && (!!analysis.finalAttributes || !!analysis.correctedAttributes)

export const hasMotivationOverrides = (analysis: MotivationAnalysis | null | undefined): boolean =>
  hasMotivationAnalysis(analysis) && !sameAttributes(analysis.finalAttributes, analysis.correctedAttributes)

export const getEditableAttributes = (analysis: MotivationAnalysis | null | undefined): AttributeFormValue[] => {
  if (!hasMotivationAnalysis(analysis)) return []
  const attributes = (hasMotivationOverrides(analysis) ? analysis.finalAttributes : analysis.correctedAttributes) ?? {}
  return Object.keys(attributes)
    .sort()
    .map((name) => ({ name, value: String(attributes[name]) }))
}

export const getAttributeValueError = (value: string): AttributeValueError | undefined => {
  if (!value.trim()) return 'required'
  const number = Number(value)
  if (!Number.isFinite(number)) return 'invalid'
  if (number < minAttributeValue || number > maxAttributeValue) return 'range'
  return undefined
}

export const toFinalAttributes = (values: AttributeFormValue[]): MotivationAttributes =>
  Object.fromEntries(values.map(({ name, value }) => [name, parseFloat(value)]))

export const getUnstoredAttributes = (sent: MotivationAttributes, analysis: MotivationAnalysis | null | undefined): string[] =>
  Object.keys(sent).filter((name) => !sameValue(analysis?.finalAttributes?.[name], sent[name]))
